// Metadata-only clip references and their source retention (expiry +
// extensions). Clips are per-user REST resources; no socket events.
import { liveApi, exportFileUrl } from '@/etc/liveApi'

const CLIP_ERRORS = {
  OUT_OF_WINDOW: 'That range is outside the retained video window.',
  RANGE_TOO_LONG: 'Clips can be at most 3 minutes long.',
  RANGE_TOO_SHORT: 'Clips must be at least 1 second long.',
  EXTENSION_LIMIT: 'No extensions left for this clip.',
  CLIP_EXPIRED: 'The source video for this clip has expired.',
  SOURCE_EXPIRED: 'The source video for this clip has expired.',
  STREAM_NOT_FOUND: 'This stream is no longer available.',
  ALREADY_STORED: 'This clip is already saved to Vyby.',
  FILE_REGISTER_FAILED: 'Could not save the clip to your Vyby library.',
  CLIP_REGISTER_FAILED: 'Could not save the clip to your Vyby library.',
  ASSET_REGISTER_FAILED: 'Could not save the clip to your Vyby library.',
  EXPORT_FAILED: 'Preparing the clip video failed.',
}

const JOB_POLL_MS = 800

const waitForJob = async (jwt, jobId, onUpdate) => {
  for (;;) {
    const { job } = await liveApi.exportJob(jwt, jobId)
    onUpdate(job)
    if (job.status !== 'running') return job
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS))
  }
}

const triggerDownload = (url) => {
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const clipErrorMessage = (code) => CLIP_ERRORS[code] || `Clip action failed (${code}).`

export default {
  namespaced: true,
  state: {
    byId: {},
    loading: false,
    error: null,
    storage: null,
    busy: {},
    // clipId → { kind, jobId, status, ratio, error, result }
    jobs: {},
    projects: null,
  },
  getters: {
    all: (state) => Object.values(state.byId).sort((a, b) => b.createdAt - a.createdAt),
    forStream: (state, getters) => (streamId) => getters.all.filter((c) => c.streamId === streamId),
  },
  mutations: {
    set: (state, list) => {
      state.byId = Object.fromEntries((list || []).map((c) => [c.clipId, c]))
    },
    merge: (state, list) => {
      state.byId = { ...state.byId, ...Object.fromEntries((list || []).map((c) => [c.clipId, c])) }
    },
    upsert: (state, clip) => {
      state.byId = { ...state.byId, [clip.clipId]: clip }
    },
    remove: (state, clipId) => {
      const next = { ...state.byId }
      delete next[clipId]
      state.byId = next
    },
    loading: (state, next) => {
      state.loading = !!next
    },
    error: (state, next) => {
      state.error = next
    },
    storage: (state, next) => {
      state.storage = next || null
    },
    busy: (state, { clipId, value }) => {
      state.busy = { ...state.busy, [clipId]: value }
    },
    job: (state, { clipId, job }) => {
      state.jobs = { ...state.jobs, [clipId]: job }
    },
    projects: (state, list) => {
      state.projects = list || []
    },
  },
  actions: {
    load: async (store, { streamId = null } = {}) => {
      store.commit('loading', true)
      store.commit('error', null)
      try {
        const data = await liveApi.clips(store.rootState.user.jwt, streamId)
        store.commit(streamId ? 'merge' : 'set', data.clips)
        store.commit('storage', data.storage)
      } catch (err) {
        store.commit('error', clipErrorMessage(err.message))
      } finally {
        store.commit('loading', false)
      }
    },

    create: async (store, { streamId, startTime, endTime }) => {
      store.commit('error', null)
      try {
        const data = await liveApi.createClip(store.rootState.user.jwt, { streamId, startTime, endTime })
        store.commit('upsert', data.clip)
        store.commit('storage', data.storage)
        return data.clip
      } catch (err) {
        store.commit('error', clipErrorMessage(err.message))
        return null
      }
    },

    remove: async (store, clipId) => {
      store.commit('busy', { clipId, value: 'delete' })
      try {
        await liveApi.deleteClip(store.rootState.user.jwt, clipId)
        store.commit('remove', clipId)
      } catch (err) {
        store.commit('error', clipErrorMessage(err.message))
      } finally {
        store.commit('busy', { clipId, value: null })
      }
    },

    // Temporary exact-cut MP4 → browser download; the server copy is
    // deleted once sent.
    download: async (store, clipId) => {
      const jwt = store.rootState.user.jwt
      const update = (job) => store.commit('job', { clipId, job: { ...job, kind: 'download' } })
      try {
        const { job } = await liveApi.downloadClip(jwt, clipId)
        update(job)
        const done = await waitForJob(jwt, job.jobId, update)
        if (done.status !== 'done') throw new Error(done.error || 'EXPORT_FAILED')
        triggerDownload(exportFileUrl(done.jobId, jwt))
        return done
      } catch (err) {
        store.commit('job', { clipId, job: { kind: 'download', status: 'failed', error: err.message } })
        store.commit('error', clipErrorMessage(err.message))
        return null
      }
    },

    // "Edit in Vyby": permanent asset in the user's library, optionally
    // attached to a project. Resolves with the job result.
    materialize: async (store, { clipId, projectId = null }) => {
      const jwt = store.rootState.user.jwt
      const update = (job) => store.commit('job', { clipId, job: { ...job, kind: 'materialize' } })
      try {
        const { job } = await liveApi.materializeClip(jwt, clipId, projectId)
        update(job)
        const done = await waitForJob(jwt, job.jobId, update)
        if (done.status !== 'done') throw new Error(done.error || 'EXPORT_FAILED')
        if (done.result?.clip) store.commit('upsert', done.result.clip)
        return done.result
      } catch (err) {
        store.commit('job', { clipId, job: { kind: 'materialize', status: 'failed', error: err.message } })
        store.commit('error', clipErrorMessage(err.message))
        return null
      }
    },

    loadProjects: async (store) => {
      try {
        const data = await liveApi.projects(store.rootState.user.jwt)
        store.commit('projects', data.projects)
      } catch (err) {
        store.commit('projects', [])
        store.commit('error', clipErrorMessage(err.message))
      }
    },

    extend: async (store, clipId) => {
      store.commit('busy', { clipId, value: 'extend' })
      try {
        const data = await liveApi.extendClip(store.rootState.user.jwt, clipId)
        store.commit('upsert', data.clip)
      } catch (err) {
        store.commit('error', clipErrorMessage(err.message))
      } finally {
        store.commit('busy', { clipId, value: null })
      }
    },
  },
}
